import Header from '../../Header/Header';
import { Icon, CustomTable, ConfirmationPopup, SuccessModal, Pagination, Search, Loader } from '@ohif/ui';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { customStyle } from '@custom/Utils/utils';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@custom/store/store';
import { useTheme } from "../../../ui/src/contextProviders";
import { _routes } from '@custom/Utils/constants/routes';
import getTeleModPrices from '@custom/rtk-apis/Setting/getTeleModPrices';
import deleteTeleModPrice from '@custom/rtk-apis/Setting/deleteTeleModPrice';
import editTeleModPrice from '@custom/rtk-apis/Setting/editTeleModPrice';
import { Formik, Form, Field } from 'formik';
import { servicesManager } from '../../../app/src/App';

const ModalityPricing = () => {
    const dispatch = useDispatch<AppDispatch>();

    const { uiNotificationService } = servicesManager.services;

    const navigate = useNavigate();
    const getTeleModPriceSliceResponse = useSelector((state: RootState) => state.getTeleModPriceSliceResponse);

    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [isSuccessModalOpen, setIsSuccessModal] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [tableData, setTableData] = useState([]);
    const [search, setSearch] = useState('');
    const [pgNumber, setPgNumber] = useState(1);
    const [pgLimit, setPgLimit] = useState(30);
    const [totalPg, setTotalPg] = useState(100);
    const [rupeeValue, setRupeeValue] = useState(1);
    const [creditSymbol, setCreditSymbol] = useState('₹');
    const isDarkMode = useTheme();
    const [isLoading, setIsLoading] = useState(false);
    const [creditUpdateSuccess, setCreditUpdateSuccess] = useState(false);

    const columns = [
        { key: 'modality', title: 'Modality' },
        { key: 'studyRegion', title: 'Study Region' },
        { key: 'credit', title: 'Credit' },
        { key: 'value', title: 'Credit Value' },
        { key: 'actions', title: 'Action' },
    ];

    const actions = [
        {
            label: 'Edit',
            iconName: 'edit',
            onClick: (item) => {
                navigate(_routes.settings.addEditModalityPricing, { state: { modalityData: item, editMode: true } });
            }
        },
        {
            label: 'Delete',
            iconName: 'delete',
            onClick: (item) => {
                setSelectedRow(item);
                setIsPopupOpen(true);
            }
        },
    ];

    const handleConfirm = async () => {
        setIsLoading(true);
        try {
            setIsPopupOpen(false);
            const payload = { _id: selectedRow?._id };
            dispatch(deleteTeleModPrice(payload)).then((response: any) => {
                if (response?.meta?.requestStatus === 'fulfilled') {
                    setIsSuccessModal(true);
                    const getPayload = { search, limit: pgLimit, pageNumber: pgNumber }
                    dispatch(getTeleModPrices(getPayload));
                }
            });
        } catch (error) {
            console.log(error);
        }
        finally {
            setIsLoading(false);
        }
    };

    const onPageChangeHandler = (pg) => {
        setPgNumber(pg);
        // run the get api for the modality pricing with the search, limit and page number
        const payload = { search, limit: pgLimit, pageNumber: pg };
        dispatch(getTeleModPrices(payload));
    };

    const handlePageSizeChange = (e) => {
        setPgLimit(e);
        // run the get api for the modality pricing with the search, limit and page number
        const payload = { search, limit: e, pageNumber: pgNumber };
        dispatch(getTeleModPrices(payload));
    };

    const searchClick = (searchText) => {
        const trimmedSearch = searchText.trim();
        setPgNumber(1);
        setIsLoading(true);

        const payload = {
            limit: pgLimit,
            pageNumber: 1,
            ...(trimmedSearch && { search: trimmedSearch })
        };

        dispatch(getTeleModPrices(payload));
    };

    const handleRupeeUpdate = (values) => {
        setRupeeValue(values.rupeeValue);
        const payload = { creditValue: values.rupeeValue, creditSymbol: values.creditSymbol };
        dispatch(editTeleModPrice(payload)).then((response: any) => {
            if (response?.meta?.requestStatus === 'fulfilled') {
                setCreditUpdateSuccess(true);
                dispatch(getTeleModPrices({ search, limit: pgLimit, pageNumber: pgNumber }));
            }
            else if(response?.meta?.requestStatus === 'rejected'){
                uiNotificationService.show({
                    title: 'Credit',
                    message: response?.payload,
                    type: 'error',
                });
            }
        });
    };

    useEffect(() => {
        if (getTeleModPriceSliceResponse?.status === 'SUCCESS' && getTeleModPriceSliceResponse?.result) {
            console.log("🚀 ~ useEffect ~ getTeleModPriceSliceResponse:", getTeleModPriceSliceResponse?.result?.data)
            setTableData(
                Array.isArray(getTeleModPriceSliceResponse?.result?.data)
                  ? getTeleModPriceSliceResponse.result.data.map(item => ({
                      ...item,
                      studyRegion: item.modality === 'Histopathology' ? '-' : item.studyRegion,
                      value: `₹ ${item.credit * (getTeleModPriceSliceResponse?.result?.creditValue || 0)}`
                    }))
                  : []
              );
            setTotalPg(getTeleModPriceSliceResponse?.result?.pagination?.total);
            setPgNumber(getTeleModPriceSliceResponse?.result?.pagination?.page);
            setRupeeValue(getTeleModPriceSliceResponse?.result?.creditValue);
            setCreditSymbol(getTeleModPriceSliceResponse?.result?.creditSymbol);
        }
        setIsLoading(false);
    }, [getTeleModPriceSliceResponse?.status, getTeleModPriceSliceResponse?.result]);

    useEffect(() => {
        setIsLoading(true);
        const payload = { search, limit: pgLimit, pageNumber: pgNumber };
        dispatch(getTeleModPrices(payload));
    }, [dispatch, pgLimit, pgNumber]);

    const currencyOptions = [
        { value: "₹", label: "₹" },
        // { value: "$", label: "$" },
        // { value: "AED", label: "AED" },
        // { value: "€", label: "€" }
    ];

    return (
        <>
            <div className='page page__fullBody' style={
                isDarkMode?.isDarkMode ? { background: '#090c29' } : customStyle.mainBackground
            }>
                <Header isBack={false} title='Tele Hospital Credits' />
                <div className='page__body'>
                    <div className='tableWrap'>
                        <div className='tableWrap__head'>
                            <div className="formRow formRow__2 m_0 w_100 formRow--flexEnd">
                                <div className='formCol'>
                                    <Search text="Search by Modality, Study Region" onSearchClick={searchClick} value={search} setValue={setSearch} />
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <Formik
                                        initialValues={{ rupeeValue, creditSymbol }}
                                        onSubmit={handleRupeeUpdate}
                                        enableReinitialize
                                    >
                                        {({ values, handleChange }) => (
                                            <Form style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                <div className="flex items-center space-x-2">
                                                    <label className="form__label text-base whitespace-nowrap">1 Credit =</label>
                                                    <select className="form__select form__select--sm" id="creditSymbol"
                                                        name="creditSymbol"
                                                        value={values.creditSymbol}
                                                        style={customStyle.select}
                                                        onChange={(e) => {
                                                            handleChange(e);
                                                        }}>
                                                        <option value="" disabled>-Select-</option>
                                                        {currencyOptions.map((option) => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.value}
                                                        </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <Field
                                                    type="number"
                                                    name="rupeeValue"
                                                    className='form__input form__input--sm'
                                                    value={values.rupeeValue}
                                                    style={{ width: '100px' }}
                                                />
                                                <button
                                                    type="submit"
                                                    className="btn btn__primary btn--rounded"
                                                >
                                                    Update
                                                </button>
                                            </Form>
                                        )}
                                    </Formik>
                                </div>
                            </div>
                            <div className='btn__group'>
                                <button className="btn btn__white" onClick={() => navigate(_routes.settings.addEditModalityPricing)}>
                                    <Icon name="icon-add" />
                                    Add Credit
                                </button>
                            </div>
                        </div>
                        <div className='tableContainer'>
                            <CustomTable
                                columns={columns}
                                data={tableData}
                                actions={actions}
                                searchQuery={searchQuery}
                                from='modalityPricing'
                                userDetails={''}
                                setClickedItem={''}
                                clickedItem={''}
                            />
                        </div>
                    </div>
                </div>
                <Loader isLoaderOpen={isLoading} />
                {tableData.length > 0 && (
                    <Pagination
                        onChangePage={onPageChangeHandler}
                        onChangePerPage={handlePageSizeChange}
                        totalPage={totalPg}
                        currentPage={pgNumber}
                        perPage={pgLimit}
                    />
                )}
                <ConfirmationPopup
                    title={''}
                    isOpen={isPopupOpen}
                    message={`Are you sure you want to delete ${selectedRow?.modality}?`}
                    onConfirm={handleConfirm}
                    onCancel={() => setIsPopupOpen(false)}
                />
                <SuccessModal
                    isSuccessModalOpen={isSuccessModalOpen}
                    title={`Modality pricing deleted successfully.`}
                    handleCloseModal={() => setIsSuccessModal(false)}
                />
                <SuccessModal
                    isSuccessModalOpen={creditUpdateSuccess}
                    title={`Credit price updated successfully.`}
                    handleCloseModal={() => setCreditUpdateSuccess(false)}
                />
            </div>
        </>
    );
};

export default ModalityPricing;
